/**
 * Retry helpers for transient browser-side network failures.
 *
 * Targets the failure modes seen in practice:
 *   - `TypeError: Failed to fetch` (Chromium when the connection drops mid-flight)
 *   - `net::ERR_NETWORK_CHANGED` (Wi-Fi/VPN flip)
 *   - axios `ERR_NETWORK` / `ECONNABORTED` / `ETIMEDOUT`
 *
 * Permanent failures (4xx, 5xx response that completed the round-trip, abort
 * signals, parse errors) are *not* retried — those need real handling, not
 * blind retries.
 */

export interface RetryOptions {
  /** Number of *retries* after the first attempt. Default 3 → 4 tries total. */
  maxRetries?: number;
  /** Delay before the first retry; doubles each attempt up to 8s. */
  initialDelayMs?: number;
  /** When set, retries stop and the abort error propagates. */
  signal?: AbortSignal;
  /** Optional progress callback; useful for surfacing "reconnecting…" UI. */
  onRetry?: (attempt: number, err: unknown) => void;
}

export async function withNetworkRetry<T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const max = opts.maxRetries ?? 3;
  let delay = opts.initialDelayMs ?? 800;
  let lastErr: unknown;

  for (let attempt = 0; attempt <= max; attempt++) {
    if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isTransientNetworkError(err)) throw err;
      if (attempt === max) throw err;
      opts.onRetry?.(attempt + 1, err);
      // Wait either for the network to come back or for a short backoff,
      // whichever happens first. Most ERR_NETWORK_CHANGED events recover
      // within a second of the `online` event firing.
      await waitForOnlineOrTimeout(delay, opts.signal);
      delay = Math.min(delay * 2, 8000);
    }
  }
  throw lastErr;
}

/**
 * Heuristic — only the failure modes we know how to recover from. Anything
 * exotic (CORS, DNS) bubbles up so the caller can decide what to do.
 */
/** Core classifier — no `cause` chaining (avoids cycles). */
function transientNetworkErrorInner(err: unknown): boolean {
  if (err instanceof DOMException) {
    if (err.name === 'AbortError') return false;
    // ReadableStream / fetch failures (esp. mid-body) often surface as DOMException NetworkError.
    if (err.name === 'NetworkError') return true;
  }
  if (err instanceof TypeError) {
    const m = err.message || '';
    // Chromium connect-time drops usually say "Failed to fetch"; mid-stream + some paths use "network error".
    return /failed to fetch|network\s*error|networkerror|load failed/i.test(m);
  }
  if (err && typeof err === 'object') {
    const e = err as { code?: string; message?: string; name?: string };
    if (e.name === 'AbortError') return false;
    if (e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') return true;
    if (e.message && /Network Error|net::ERR_NETWORK_CHANGED|ERR_NETWORK_CHANGED|Failed to fetch|Load failed|NetworkError|connection (?:was )?lost|network.*changed|stream (?:was )?closed/i.test(e.message)) {
      return true;
    }
  }
  return false;
}

export function isTransientNetworkError(err: unknown): boolean {
  if (transientNetworkErrorInner(err)) return true;
  // AggregateError / Error with DOMException cause (seen with some fetch stacks).
  if (err instanceof Error && err.cause !== undefined) {
    return transientNetworkErrorInner(err.cause);
  }
  return false;
}

/**
 * Public helper for callers that manage their own retry loop (e.g. SSE reads
 * where we want to retry the *whole* fetch but keep our own bookkeeping).
 * Resolves on the soonest of: `online` event, `timeoutMs` elapsing, or abort.
 */
export function waitForOnlineOrShortDelay(timeoutMs: number, signal?: AbortSignal): Promise<void> {
  return waitForOnlineOrTimeout(timeoutMs, signal);
}

/**
 * Resolves on the soonest of:
 *   - the `online` event firing (network came back)
 *   - `timeoutMs` elapsing (so we still try even if the OS hasn't fired `online`)
 *   - the abort signal — in which case we reject with AbortError.
 */
function waitForOnlineOrTimeout(timeoutMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener('online', onOnline);
      window.clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onAbort);
    };
    const onOnline = () => { cleanup(); resolve(); };
    const onAbort = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      window.addEventListener('online', onOnline, { once: true });
    }
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    const timer = window.setTimeout(() => { cleanup(); resolve(); }, timeoutMs);
  });
}
